import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useAccount, useConnect, useDisconnect } from 'wagmi';
import { AuthContext, type AuthContextValue } from './AuthContext';
import { useAuthState } from './useAuthState';
import { useAuthActions } from './useAuthActions';
import { usePasswordActions } from './usePasswordActions';
import { useOAuthActions } from './useOAuthActions';
import { STORAGE_KEYS, setItem, removeItem } from '../../lib/storage';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading, setUser, setLoading, isAuthenticated, refreshUserData, updateUserData, clearAuthData } = useAuthState();

  // Wagmi hooks - must be before useAuthActions so we can pass disconnectWallet
  const [connectedWalletAddress, setConnectedWalletAddress] = useState<string | null>(null);
  const [isWalletConnecting, setIsWalletConnecting] = useState(false);

  const { address: wagmiWalletAddress, isConnected: isWagmiWalletConnected, isConnecting, isReconnecting } = useAccount();
  const { connectAsync, connectors, isPending, error: connectWalletError } = useConnect();
  const { disconnect: disconnectWalletWagmi } = useDisconnect();

  const disconnectWallet = useCallback(async () => {
    setIsWalletConnecting(true);
    try {
      disconnectWalletWagmi();
      removeItem(STORAGE_KEYS.WALLET_ADDRESS);
      return { success: true };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Disconnection failed.' };
    } finally {
      setIsWalletConnecting(false);
    }
  }, [disconnectWalletWagmi]);

  const connectWallet = useCallback(async () => {
    const coinbaseConnector = connectors.find((c) => c.id === 'coinbaseWalletSDK');
    if (!coinbaseConnector) {
      return { success: false, error: 'Coinbase Wallet connector not found.' };
    }

    setIsWalletConnecting(true);
    try {
      await connectAsync({ connector: coinbaseConnector });
      return { success: true };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Connection failed.' };
    } finally {
      setIsWalletConnecting(false);
    }
  }, [connectors, connectAsync]);

  const { login, logout, register, resendVerificationEmail } = useAuthActions({ setUser, setLoading, clearAuthData, disconnectWallet });
  const { requestPasswordReset, verifyResetToken, resetPassword, updatePassword, updateEmail } = usePasswordActions({
    user,
    setUser,
    setLoading,
    clearAuthData,
    refreshUserData,
  });
  const { processOAuthTokens } = useOAuthActions({ setUser, setLoading, clearAuthData });

  useEffect(() => {
    const newAddress = wagmiWalletAddress || null;
    setConnectedWalletAddress(newAddress);
    if (newAddress) {
      setItem(STORAGE_KEYS.WALLET_ADDRESS, newAddress);
    } else {
      removeItem(STORAGE_KEYS.WALLET_ADDRESS);
    }
  }, [wagmiWalletAddress]);

  useEffect(() => {
    setIsWalletConnecting(isConnecting || isReconnecting || isPending);
  }, [isConnecting, isReconnecting, isPending]);

  const contextValue: AuthContextValue = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated,
      login,
      logout,
      register,
      resendVerificationEmail,
      requestPasswordReset,
      verifyResetToken,
      resetPassword,
      updatePassword,
      updateEmail,
      processOAuthTokens,
      refreshUserData,
      updateUserData,
      connectedWalletAddress,
      isWalletConnected: isWagmiWalletConnected,
      isWalletConnecting,
      connectWallet,
      disconnectWallet,
      connectWalletError: connectWalletError || null,
    }),
    [
      user,
      loading,
      isAuthenticated,
      login,
      logout,
      register,
      resendVerificationEmail,
      requestPasswordReset,
      verifyResetToken,
      resetPassword,
      updatePassword,
      updateEmail,
      processOAuthTokens,
      refreshUserData,
      updateUserData,
      connectedWalletAddress,
      isWagmiWalletConnected,
      isWalletConnecting,
      connectWallet,
      disconnectWallet,
      connectWalletError,
    ]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
